import { useEffect, useRef, useState } from 'react';
import type { FleetPosition } from './types';
import { API_BASE } from './api';

type Status = 'connecting' | 'open' | 'offline';

function wsUrl(path: string): string {
  const base = API_BASE || location.origin;
  const u = new URL(base);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.pathname = path;
  return u.toString();
}

/**
 * Live fleet socket, shared by the buggy (publishes its own position) and the
 * pro shop dashboard (subscribes to everything).
 *
 * Reconnects with capped exponential backoff. Position sends are throttled and
 * dropped rather than queued — a stale position is worse than no position, and
 * the last one always wins.
 */
export function useFleet(opts: {
  courseId: string;
  role: 'buggy' | 'shop';
  buggyId?: string;
  /** Fired when a peer (or another of this golfer's own surfaces) changes hole. */
  onHole?: (buggyId: string, hole: number) => void;
}) {
  const { courseId, role, buggyId } = opts;
  const [status, setStatus] = useState<Status>('connecting');
  const [positions, setPositions] = useState<Record<string, FleetPosition>>({});
  const sock = useRef<WebSocket | null>(null);
  const lastSend = useRef(0);
  const backoff = useRef(1000);
  const closed = useRef(false);
  // Held in a ref so the socket's onmessage always calls the latest handler
  // without re-opening the connection every render.
  const onHoleRef = useRef(opts.onHole);
  onHoleRef.current = opts.onHole;

  useEffect(() => {
    closed.current = false;
    let retry: number | undefined;

    const connect = () => {
      if (closed.current) return;
      setStatus('connecting');
      const qs = new URLSearchParams({ course: courseId, role });
      if (buggyId) qs.set('buggy', buggyId);
      let ws: WebSocket;
      try {
        ws = new WebSocket(`${wsUrl('/fleet')}?${qs}`);
      } catch {
        setStatus('offline');
        retry = window.setTimeout(connect, backoff.current);
        return;
      }
      sock.current = ws;

      ws.onopen = () => {
        backoff.current = 1000;
        setStatus('open');
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string);
          if (msg.type === 'snapshot') {
            const next: Record<string, FleetPosition> = {};
            for (const p of msg.positions as FleetPosition[]) next[p.buggyId] = p;
            setPositions(next);
          } else if (msg.type === 'pos') {
            const p = msg.position as FleetPosition;
            setPositions((prev) => ({ ...prev, [p.buggyId]: p }));
          } else if (msg.type === 'gone') {
            setPositions((prev) => {
              const n = { ...prev };
              delete n[msg.buggyId as string];
              return n;
            });
          } else if (msg.type === 'hole') {
            onHoleRef.current?.(msg.buggyId as string, msg.hole as number);
          }
        } catch {
          /* ignore malformed frame */
        }
      };
      const reconnect = () => {
        if (closed.current) return;
        setStatus('offline');
        backoff.current = Math.min(backoff.current * 2, 30_000);
        retry = window.setTimeout(connect, backoff.current);
      };
      ws.onclose = reconnect;
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      closed.current = true;
      if (retry) window.clearTimeout(retry);
      sock.current?.close();
    };
  }, [courseId, role, buggyId]);

  /** Throttled to ~1 msg / 4s — 60 buggies at that rate is trivial load. */
  const publish = (p: Omit<FleetPosition, 'ts'>, minIntervalMs = 4000) => {
    const now = Date.now();
    if (now - lastSend.current < minIntervalMs) return false;
    const s = sock.current;
    if (!s || s.readyState !== WebSocket.OPEN) return false;
    lastSend.current = now;
    s.send(JSON.stringify({ type: 'pos', position: { ...p, ts: now } }));
    return true;
  };

  const sendAlert = (kind: string, detail: string) => {
    const s = sock.current;
    if (s?.readyState === WebSocket.OPEN) {
      s.send(JSON.stringify({ type: 'alert', kind, detail, buggyId, ts: Date.now() }));
    }
  };

  /** Broadcast the hole the golfer just moved to, so their other surface (and
   *  the shop) follows. Not throttled — hole changes are rare and every one
   *  matters, unlike position spam. */
  const publishHole = (hole: number) => {
    const s = sock.current;
    if (s?.readyState === WebSocket.OPEN) {
      s.send(JSON.stringify({ type: 'hole', buggyId, hole, ts: Date.now() }));
    }
  };

  return { status, positions, publish, sendAlert, publishHole };
}
